/* Copyright (c) 2020-present CloudIO Inc */

import { getCustomChildItemTypes } from 'componentregistry/registry/Manager';
import { replaceItemType } from 'designer/state';
import { helpRenders } from '../../help/sample_pages/BoxPage';
import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './Box';
import { analyzeBox } from './BoxAnalyze';
import { BoxProps, isPropertyVisible } from './BoxExports';

const BoxManager: ComponentManagerFn<'Box'> = (
  platform: Platform,
  type: 'Box',
) => {
  return {
    ...baseComponentManager<'Box'>(platform, type),
    type,
    name: 'Box',
    isChildAllowed: (page, item, childType) =>
      (
        [
          ...getCustomChildItemTypes('Box'),
          'ActionButton',
          'ActiveUsers',
          'Alert',
          'AsyncSelect',
          'AttachmentField',
          'AutoSizer',
          'Avatar',
          'Box',
          'BoxList',
          'BreadcrumbNavigation',
          'Button',
          'ButtonDropdown',
          'ButtonGroup',
          'Checkbox',
          'Chip',
          'Chips',
          'ClientSideExcelUpload',
          'CollectionList',
          'ControllerComponent',
          'DarkModeToggle',
          'DateField',
          'Divider',
          'DragSource',
          'DragSourceBox',
          'DropTargetBox',
          'DropTarget',
          'EmptySpacer',
          'ExpressionWatchListener',
          'FullScreenToggle',
          'FusionChartsIO',
          'MultiSeriesChart',
          'GraphicMessage',
          'H1',
          'H2',
          'H3',
          'H4',
          'H5',
          'H6',
          'HTML',
          'IOSSwitch',
          'Icon',
          'IconButton',
          'IconSelect',
          'IconSwitch',
          'Image',
          'InputBase',
          'ImageCropper',
          'LanguageSelect',
          'Link',
          'LOVField',
          'LookupSelect',
          'Media',
          'MultiField',
          'Markdown',
          'Menu',
          'MetricBox',
          'Mirror',
          'MutliSelectLOVField',
          'PageMenu',
          'Pagination',
          'Popover',
          'ProgressIndicator',
          'PulseTip',
          'RadioBox',
          'RichTextEditor',
          'Rating',
          'Select',
          'SmartSearch',
          'Stepper',
          'SwitchRole',
          'TextField',
          'NumberField',
          'Toolbar',
          'TriggerOnClick',
          'Typography',
          'UploadFileToFunction',
          'UserList',
          'UserPic',
          'UserProfileMenu',
          'ViewAttachment',
          'WaveDots',
          'CalendarSourceEvent',
          'MultiDateSelector',
          'AngularGauge',
          'GridPersonalizationMenu',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    defaultChildren: ['Typography'],
    isPropertyVisible,
    props: BoxProps,
    category: 'Dashboard',
    canHaveChildren: () => true,
    onReplace: (item, fromItem, page) => {
      if (fromItem.type === 'RowPanel') {
        fromItem.children.forEach((child) => {
          if (page[child].type === 'ColumnPanel') {
            replaceItemType(page, child, 'Box');
          }
        });
      }
      return item;
    },
    prependOverlay: true,
    icon: { iconName: 'box' },
    showEmptyPlaceholderInDesignerView: false,
    replaceableWith: ['BoxList', 'DragSourceBox', 'DropTargetBox'],
    description: `**Overview**<br/>
The Box component is the fundamental building block of your application's user interface. It serves as a flexible, lightweight wrapper that allows you to apply virtually any CSS styling or layout rule to the components inside it.
While specialized containers (like Cards or Split Panels) enforce specific structures, the Box is unopinionated. You use it to group elements, define custom spacing, build responsive grids using Flexbox, or simply add a background color to a section of text.

**1. Layout & Positioning (Flexbox)**<br/>
The Box component exposes a powerful set of Flexbox utilities, allowing you to control exactly how child components are arranged within the container.
* **Display:** Defines the layout behavior (Flex, Block, Inline).
* **Flex Direction:** Determines the main axis of the layout. (Row arranges items horizontally; Column stacks items vertically).
* **Justify Content:** Controls alignment along the main axis. Use this to distribute space (e.g., Space Between, Center, Flex End).
* **Align Items:** Controls alignment along the cross axis (e.g., vertically centering items in a horizontal row).
* **Flex Wrap:** Determines whether items are forced onto a single line or allowed to wrap onto multiple lines when space runs out.
* **Gap:** Sets a uniform amount of space (gutter) between all child items.

**2. Spacing & Sizing**<br/>
Use these properties to manage the physical dimensions and "breathing room" of the container.
* **Width / Height:** Sets specific dimensions (e.g., 100%, 500px).
* **Min/Max Dimensions:** Constraints the box's size to ensure responsiveness (e.g., preventing a panel from getting too wide on large screens).
* **Padding:** Adds internal spacing between the box's border and its content.
* **Margin:** Adds external spacing to push the box away from neighboring components.

**3. Visual Styling**<br/>
Customize the look of the container to match your design system.
* **Background Color:** Sets the fill color of the box.
* **Border:** Defines the thickness, style, and color of the boundary line.
* **Border Radius:** Controls the roundness of the corners.
* **Box Shadow / Elevation:** Adds depth using drop shadows.

**4. Typography**<br/>
The Box allows you to set text styles that will cascade down to any text-based children inside it.
* **Font Size / Weight:** Sets the scale and thickness of the text.
* **Text Color:** Defines the default color for text content.
* **Text Alignment:** Controls the horizontal alignment of text (Left, Center, Right, Justify).

**5. Composition**<br/>
The Box is designed for universal compatibility.
* **Allowed Children:** The Box accepts almost every component in the library, including Charts, Forms, Buttons, and other Boxes.
* **Nesting:** You can—and often will—nest Boxes inside other Boxes to create complex layouts (e.g., a "Parent Box" for the page section containing a "Child Box" for a specific toolbar).`,
    helpRenders,
    analyze: analyzeBox,
  };
};

export default BoxManager;
